import React, { useEffect, useState } from 'react';
import { ClipboardList, Users } from 'lucide-react';
import { apiGet } from '../lib/api';

/**
 * Iter 157 — Sidebar live-counts strip.
 *
 * Tiny operational glance directly under the Ellora logo:
 *   [📋 12 DPRs today]   [👥 134 / 187 present (72%)]
 *
 * Polls `/ops/sidebar-summary` every 60 s. Stays silent (renders nothing)
 * until the first successful fetch so the layout doesn't shift on slow
 * networks.
 */
export default function SidebarLiveCounts() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await apiGet('/ops/sidebar-summary');
        if (alive) setData(r);
      } catch { /* silent — pills just don't render */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60_000);
    const onVisible = () => { if (!document.hidden) fetchOnce(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  if (!data) return null;
  // Hide entirely when there's nothing to show (empty preview DB / brand new tenant).
  if (!data.dpr_today_count && !data.labour_total) return null;

  return (
    <div
      className="px-4 py-2 border-b border-slate-200 flex flex-wrap items-center gap-1.5"
      data-testid="sidebar-live-counts"
    >
      <Pill
        testid="sidebar-pill-dpr"
        icon={ClipboardList}
        label={`${data.dpr_today_count} DPR${data.dpr_today_count === 1 ? '' : 's'} today`}
        accent="indigo"
      />
      <Pill
        testid="sidebar-pill-labour"
        icon={Users}
        label={`${data.labour_present}/${data.labour_total} present`}
        sub={`${data.attendance_pct}%`}
        accent={data.attendance_pct >= 75 ? 'emerald' : data.attendance_pct >= 50 ? 'amber' : 'rose'}
      />
    </div>
  );
}

function Pill({ icon: Icon, label, sub, accent, testid }) {
  const color = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-50  text-amber-800   border-amber-200',
    rose:    'bg-rose-50   text-rose-800    border-rose-200',
    indigo:  'bg-[#1F7A6E]/5 text-[#1F7A6E] border-[#1F7A6E]/20',
  }[accent] || 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-display font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${color}`}
      data-testid={testid}
    >
      <Icon size={10} />
      <span className="normal-case font-medium tracking-normal">{label}</span>
      {sub ? <span className="opacity-70 normal-case font-normal">· {sub}</span> : null}
    </span>
  );
}
